// Rich readings (firmware .58): a raw radar capture on every normal wake -
// the beat signal, the spectrum or both, each in its own raw output mode -
// kept on the node and sent with its uploads as one small burst per wake
// ("kind": "rich" on the bursts endpoint; bursts from the classic hourly
// setting are "burst", and older data has no kind). The settings, what they
// cost, the node's counters, its wake cadence, the captures to expect and the
// list's hours. Pure helpers: no React, network or storage.
import { duration } from "utils/fieldFormat";
import {
  finite,
  richPartsText,
  richSettingsText,
  sgtTime,
  timeOf,
  wakeEveryText,
} from "utils/radarDiagnostics";
import { middleHalfMean } from "utils/radarStream";
import {
  CAPTURE_SECONDS,
  burstKind,
  isLiveReading,
  liveSpans,
  liveWithin,
  readWhole,
} from "utils/radarBurst";

// radar_rich: what to capture on every wake (1 the spectrum, 2 the beat
// signal; 0 off); radar_rich_up: what to capture as well on the wakes that
// upload; radar_rich_min_bv: the battery floor in mV for rich readings and
// bursts (a readable cell under it skips them); radar_rich_every (firmware
// .59): radar_rich on every Nth wake only - the upload wakes' extra parts
// are taken on every upload wake all the same.
export const RICH_SPECTRUM = 1;
export const RICH_ADC = 2;
export const RICH_KEYS = [
  "radar_rich",
  "radar_rich_up",
  "radar_rich_min_bv",
  "radar_rich_every",
];
// The firmware's limits, for a server that sends no rules.
export const RICH_RULES = {
  radar_rich: [0, 3],
  radar_rich_up: [0, 3],
  radar_rich_min_bv: [3000, 3400],
  radar_rich_every: [1, 12],
};
export const RICH_FLOOR_DEFAULT_MV = 3200;
// The recommended setup: the beat signal and the spectrum on every wake,
// nothing extra on upload wakes, the classic hourly bursts off. The beat
// signal gives the precise position and the phase, the spectrum the radar's
// own view to check our FFT against.
export const RICH_RECOMMENDED = { mask: RICH_SPECTRUM | RICH_ADC, up: 0 };
// With estimator v2 on the node (radar_edge 3 / 4, firmware .62) the beat
// signal alone: the node's estimate and ours both read it (and its phase).
// The spectrum is the radar's own view, a second capture on every wake
// (about 40 % of a rich wake's capture energy) that only cross-checks ours.
export const RICH_RECOMMENDED_V2 = { mask: RICH_ADC, up: 0 };
// The recommended rich setup for a node reporting radar_edge `edge`.
export const richRecommended = (edge) =>
  edge === 3 || edge === 4 ? RICH_RECOMMENDED_V2 : RICH_RECOMMENDED;
// The node's own counts of its rich readings, as the settings endpoint
// serves them in `counters` (null until the node reports them): captures
// taken and failed, readings queued in its flash for the next upload, and
// readings dropped because that queue was full.
export const RICH_COUNTERS = [
  ["rich_ok", "ok", "captured"],
  ["rich_fail", "fail", "failed"],
  ["rich_queued", "queued", "readings waiting to upload"],
  ["rich_dropped", "dropped", "readings dropped (queue full)"],
];

const PARTS = [
  [RICH_ADC, "beat signal"],
  [RICH_SPECTRUM, "spectrum"],
];
const bits = (v) => (Number.isInteger(v) ? v & (RICH_SPECTRUM | RICH_ADC) : 0);
const count = (v) => (bits(v) & 1 ? 1 : 0) + (bits(v) & 2 ? 1 : 0);

// The server's [low, high] for a key, else the firmware's.
export const richRule = (rules, key) =>
  Array.isArray(rules?.[key]) && rules[key].length === 2
    ? rules[key]
    : RICH_RULES[key];

// The rich-reading settings as the node reports them: {mask, up, minBv,
// every}, null where not reported or out of range.
export function readRich(reported, rules = null) {
  return {
    mask: readWhole(reported?.radar_rich, richRule(rules, "radar_rich")),
    up: readWhole(reported?.radar_rich_up, richRule(rules, "radar_rich_up")),
    minBv: readWhole(
      reported?.radar_rich_min_bv,
      richRule(rules, "radar_rich_min_bv"),
    ),
    every: readWhole(
      reported?.radar_rich_every,
      richRule(rules, "radar_rich_every"),
    ),
  };
}

// Captures on a normal wake, and the extra ones on a wake that uploads: a
// part asked for on both is taken once.
export const capturesPerWake = (mask) => count(mask);
export const uploadExtras = (mask, up) => count(bits(up) & ~bits(mask));
// The parts a wake that uploads adds to those of every wake.
export const extraParts = (mask, up) => bits(up) & ~bits(mask);
export const richOn = (rich) =>
  bits(rich?.mask ?? 0) > 0 || bits(rich?.up ?? 0) > 0;
// In words (shared with the request trails): "beat signal and spectrum";
// "beat signal and spectrum on every wake, nothing extra on upload wakes,
// battery floor 3,200 mV".
export { richPartsText, richSettingsText, wakeEveryText };

// The node's counters: {ok, fail, queued, dropped, source ("settings" |
// "reading"), t}, from the settings endpoint's `counters` (each null until
// the node reports it), else its read-back, else the newest reading that
// carries any of them; null when none has them.
export function richCounters(counters, reported = null, samples = []) {
  const pick = (record) =>
    Object.fromEntries(
      RICH_COUNTERS.map(([key, name]) => [name, finite(record?.[key])]),
    );
  const any = (values) => Object.values(values).some((v) => v !== null);
  for (const record of [counters, reported]) {
    const values = pick(record);
    if (any(values)) return { ...values, source: "settings", t: null };
  }
  let best = null;
  for (const s of samples || []) {
    const values = pick(s?.raw);
    const at = timeOf(s?.timestamp);
    if (!any(values) || at === null) continue;
    if (!best || at > best.at) best = { at, values, t: s.timestamp };
  }
  return best ? { ...best.values, source: "reading", t: best.t } : null;
}

// "1,204 captured · 3 failed · 6 readings waiting to upload · 0 readings
// dropped (queue full)".
export function countersText(counters) {
  if (!counters) return "";
  return RICH_COUNTERS.filter(([, name]) => counters[name] !== null)
    .map(([, name, words]) => `${counters[name].toLocaleString()} ${words}`)
    .join(" · ");
}

// "6 rich readings", "1 capture".
const counted = (n, word) =>
  `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;

// The node's rtct firmware build, from its version as its readings give it
// ("0.3.4-rtct.59-dev" -> 59), or null.
export function rtctBuild(fw) {
  const found = /rtct\.(\d+)(?!\d)/.exec(String(fw ?? ""));
  return found ? Number(found[1]) : null;
}

// Firmware .58 and .59 count their rich captures, but the read-back that
// carries the counters rides outside the node's settings hash, so the node
// sends it again only with a settings change: the counters stay as they were
// then (node 2 kept showing the 0 of when rich readings were switched on).
// Firmware .60 sends it again when counting starts and every 6 h. -> the build
// (58 or 59) whose counters go stale so, else null.
export function staleRichCounts(fw) {
  const build = rtctBuild(fw);
  return build === 58 || build === 59 ? build : null;
}
// From this build the node sends its counters again every 6 h.
export const COUNTERS_FRESH_BUILD = 60;

// The captures taken and failed as the node's readings tell them, from `from`
// (ms) on: each normal reading of a wake that took a rich reading (jjy_burst
// kind "rich") gives its ok and fail. -> {ok, fail, wakes}.
export function readingCounts(samples, from = -Infinity) {
  let ok = 0;
  let fail = 0;
  let wakes = 0;
  for (const s of samples || []) {
    const at = timeOf(s?.timestamp);
    const account = s?.raw?.jjy_burst;
    if (at === null || at < from || isLiveReading(s)) continue;
    if (account?.kind !== "rich") continue;
    wakes += 1;
    ok += finite(account.ok) ?? 0;
    fail += finite(account.fail) ?? 0;
  }
  return { ok, fail, wakes };
}

// "12 captured · 0 failed in the last 24 h, counted from the readings of 6
// wakes (the node's own count only refreshes when it re-sends its settings;
// on firmware .59, rarely)".
export function readingCountsText(counts, build) {
  const why = `the node's own count only refreshes when it re-sends its settings; on firmware .${build}, rarely`;
  return counts?.wakes
    ? `${counts.ok.toLocaleString()} captured · ${counts.fail.toLocaleString()} failed in the last 24 h, counted from the readings of ${counted(counts.wakes, "wake")} (${why})`
    : `None in the last 24 h, counted from the readings (${why})`;
}
// The rich readings the server holds over an analysis window (radar/
// analysis/?kind=rich): those analysed (`total`, before any thinning) and
// those still waiting for it (`pending`), and the newest analysed one's time.
// A server that ignores ?kind= lists classic bursts too: then only the rich
// points count. -> {readings, captures: null, newest (ms | null)} or null.
export function richArrived(analysis) {
  const total = finite(analysis?.total);
  if (total === null) return null;
  const points = Array.isArray(analysis?.points) ? analysis.points : [];
  const rich = points.filter((p) => p?.kind === "rich");
  const filtered = rich.length === points.length;
  const times = rich.map((p) => timeOf(p?.t)).filter((t) => t !== null);
  return {
    readings: filtered ? total + (finite(analysis?.pending) ?? 0) : rich.length,
    captures: null,
    newest: times.length ? Math.max(...times) : null,
  };
}
// "6 rich readings · newest 10 Oct, 11:21 pm".
export function arrivedText(arrived) {
  if (!arrived) return "";
  return `${counted(arrived.readings, "rich reading")}${arrived.newest !== null ? ` · newest ${dayClockText(arrived.newest)}` : ""}`;
}
// Where the counters come from, said beside them: the node's read-back that
// carries them, not its readings, so they can trail what has arrived. `at`:
// when the node took that read-back (the settings endpoint's counters_at, by
// the node's clock; null until the server has one) - from firmware .60
// (`build`) it sends one again when counting starts and every 6 h, before only
// with a settings change. Without a time, `arrived` (the rich readings the
// server holds from the last 24 h, {readings, captures}, either may be null) is
// named when the counters trail it (fewer captured than arrived).
export function countersNote(
  counters,
  arrived = null,
  { at = null, build = null } = {},
) {
  if (counters?.source !== "settings") return "";
  if (timeOf(at) !== null) {
    const when = `as of the node's report of ${sgtTime(at)}`;
    return build !== null && build >= COUNTERS_FRESH_BUILD
      ? `${when}: it sends them again when counting starts and every 6 h`
      : build === 58 || build === 59
        ? `${when}: firmware .${build} sends them again only with a settings change`
        : when;
  }
  const readings = finite(arrived?.readings);
  const captures = finite(arrived?.captures);
  const what =
    captures !== null && captures > 0
      ? `${counted(captures, "capture")}${readings ? ` (${counted(readings, "rich reading")})` : ""}`
      : readings !== null && readings > 0
        ? counted(readings, "rich reading")
        : null;
  return what !== null &&
    counters.ok !== null &&
    counters.ok < (captures || readings)
    ? `as of the node's last settings report, not its readings: they trail the ${what} that arrived in the last 24 h`
    : "as of the node's last settings report: the node sends them with its settings, not with its readings, so they can trail what has arrived";
}

// How often the node wakes outside live mode. From its settings: every
// interval_sec (lte_interval_s on cellular). As observed, preferred once its
// readings hold five normal wakes with three gaps between them that span no
// live session: the mean of the middle half of the gaps between the newest
// 25 normal readings, by when each was taken (rich readings and the readings
// themselves come in batches, so arrival times say nothing of the wakes).
export const OBSERVE_WAKES = 5;
export const OBSERVE_WAKE_GAPS = 3;
export function wakeCadence(
  reported,
  samples,
  { cellular = false, spans = null } = {},
) {
  const value = finite(
    reported?.[cellular ? "lte_interval_s" : "interval_sec"],
  );
  const settings = value !== null && value > 0 ? value : cellular ? 3600 : 300;
  const live = spans ?? liveSpans(samples);
  const taken = (samples || [])
    .filter((s) => !isLiveReading(s))
    .map((s) => timeOf(s?.timestamp))
    .filter((t) => t !== null)
    .sort((a, b) => a - b);
  const recent = taken.slice(-25);
  const gaps = [];
  for (let i = 1; i < recent.length; i++) {
    const [a, b] = [recent[i - 1], recent[i]];
    if (b > a && !live.some((s) => s.from < b && s.to > a)) gaps.push(b - a);
  }
  const spacing =
    taken.length >= OBSERVE_WAKES && gaps.length >= OBSERVE_WAKE_GAPS
      ? middleHalfMean(gaps)
      : null;
  const observed = spacing === null ? null : spacing / 1000;
  return {
    seconds: observed ?? settings,
    source: observed !== null ? "observed" : "settings",
    observed,
    settings,
    cellular,
    wakes: taken.length,
    newest: taken.length ? taken[taken.length - 1] : null,
  };
}

// "every 10 min (as observed)", "every 5 min (from its settings)", "every
// 2 min (as observed; its settings say 60 s)".
export function wakeText(cadence) {
  const c = cadence;
  const differs =
    c.observed !== null &&
    Math.abs(c.observed - c.settings) > 0.25 * c.settings;
  return `every ${duration(c.seconds)} (${
    c.source === "observed"
      ? `as observed${differs ? `; its settings say ${duration(c.settings)}` : ""}`
      : "from its settings"
  })`;
}

// When the rich readings now running began, so the captures to expect start
// there and not a whole day back (an hour after node 2's came on, a day back
// held 143 wakes that owed none, against its 6 rich readings). The node's
// normal wakes, newest first: its readings - one with a rich account
// (jjy_burst kind "rich", firmware .59; a skipped one included) is a rich
// wake - and the rich readings listed (`rich`: burstSummary()s). The run
// began at its oldest rich wake after the newest stretch of more than `every`
// normal wakes in a row without one (fewer is the wakes between every Nth, or
// an upload wake whose reading tells of its classic burst). With no rich wake
// at hand: `asked`, when the newest settings request asking for rich readings
// last changed (ms), as none is owed before it. `from` (ms): where the list of
// rich readings begins - a reading before it shows none beside it whether or
// not one was taken, so only its own account counts there.
// -> {ms, source: "first" | "asked"}, or null: they ran from before the
// readings at hand.
export function richStart(
  samples,
  rich = [],
  { every = 1, asked = null, from = -Infinity } = {},
) {
  const wakes = [
    ...(samples || [])
      .filter((s) => !isLiveReading(s))
      .map((s) => ({
        at: timeOf(s?.timestamp),
        rich: s?.raw?.jjy_burst?.kind === "rich",
      }))
      .filter((w) => w.rich || w.at >= from),
    ...(rich || []).map((s) => ({ at: s?.ms, rich: true })),
  ]
    .filter((w) => Number.isFinite(w.at))
    .sort((a, b) => b.at - a.at);
  if (!wakes.some((w) => w.rich))
    return Number.isFinite(asked) ? { ms: asked, source: "asked" } : null;
  const nth = Math.max(1, Math.round(finite(every) ?? 1));
  let oldest = null;
  let without = 0;
  for (const w of wakes) {
    if (w.rich) {
      oldest = w.at;
      without = 0;
    } else if (oldest !== null && ++without > nth) {
      return { ms: oldest, source: "first" };
    }
  }
  return null;
}

// The rich captures to expect over [from, to], outside live mode (no normal
// wake runs then): every `every`-th wake brings `perWake` captures (firmware
// .59's radar_rich_every; 1 every wake), every upload `extras` more. Wakes
// taken after `lastWake` (the newest normal reading at hand) have not been
// sent yet: the node keeps them for its next upload, so they are counted
// apart (`waiting`), not as missing.
// -> {count, wakes, readings (the wakes that take radar_rich), every,
//    uploads, perWake, extras, liveMs, live, liveNow,
//    waiting: {wakes, captures, since} | null}
export function expectedRich({
  perWake,
  extras = 0,
  every = 1,
  wakeSeconds,
  uploadSeconds = null,
  spans = [],
  from,
  to,
  lastWake = null,
}) {
  const wakeMs = (finite(wakeSeconds) ?? 0) * 1000;
  const uploadMs = (finite(uploadSeconds) ?? 0) * 1000;
  const span = Math.max(0, to - from);
  const liveMs = Math.min(span, liveWithin(spans, from, to));
  const inWindow = (spans || []).filter((s) => s.to >= from && s.from <= to);
  const sent =
    lastWake !== null && Number.isFinite(lastWake)
      ? Math.max(from, Math.min(to, lastWake))
      : to;
  const awake = (a, b) => Math.max(0, b - a - liveWithin(spans, a, b));
  const wakes = wakeMs > 0 ? awake(from, sent) / wakeMs : 0;
  const uploads = uploadMs > 0 ? awake(from, sent) / uploadMs : 0;
  const per = finite(perWake) ?? 0;
  const extra = finite(extras) ?? 0;
  const nth = Math.max(1, Math.round(finite(every) ?? 1));
  const later = wakeMs > 0 ? Math.floor(awake(sent, to) / wakeMs) : 0;
  return {
    count: (wakes / nth) * per + uploads * extra,
    wakes,
    readings: wakes / nth,
    every: nth,
    uploads,
    perWake: per,
    extras: extra,
    liveMs,
    live: inWindow.length > 0,
    liveNow: inWindow.some((s) => s.to >= to),
    waiting:
      sent < to && later > 0
        ? { wakes: later, captures: Math.floor(later / nth) * per, since: sent }
        : null,
  };
}

// How old the newest rich reading may be: they come with the node's uploads,
// so up to one upload interval (plus a little) is expected. -> "fresh"
// (within it), "overdue" (well past it) or null (unknown).
export function batchState(ageMs, uploadSeconds) {
  const upload = (finite(uploadSeconds) ?? 0) * 1000;
  if (!Number.isFinite(ageMs) || upload <= 0) return null;
  const grace = Math.max(10 * 60000, 0.25 * upload);
  return ageMs <= upload + grace ? "fresh" : "overdue";
}

// What rich readings cost, from the message sizes the firmware sends - a rich
// capture carries one packet: 2,057 B with the spectrum, 1,989 B with the
// beat signal, as the backend's projection counts them - and 200 B a reading
// for its account and framing: at `wakeSeconds` between wakes and
// `uploadSeconds` between uploads. The radar runs about 4.3 s a capture.
export const RICH_WAKE_BYTES = 200;
export const RICH_CAPTURE_BYTES = { [RICH_SPECTRUM]: 2057, [RICH_ADC]: 1989 };
const captureBytes = (part) => RICH_CAPTURE_BYTES[part];
const partsBytes = (mask) =>
  PARTS.filter(([bit]) => bits(mask) & bit).reduce(
    (sum, [bit]) => sum + captureBytes(bit),
    0,
  );
export function richEstimate({
  mask,
  up = 0,
  every = 1,
  wakeSeconds,
  uploadSeconds,
}) {
  const wake = finite(wakeSeconds);
  const upload = finite(uploadSeconds);
  const wakesDay = wake && wake > 0 ? 86400 / wake : 0;
  const uploadsDay =
    upload && upload > 0 ? Math.min(wakesDay, 86400 / upload) : wakesDay;
  const nth = Math.max(1, Math.round(finite(every) ?? 1));
  const perWake = capturesPerWake(mask);
  const extras = uploadExtras(mask, up);
  const extra = extraParts(mask, up);
  // The wakes that take radar_rich, and the readings a day: those, plus the
  // upload wakes among the others that take the extra parts alone.
  const takingDay = perWake ? wakesDay / nth : 0;
  const perDay =
    takingDay + (extras ? uploadsDay * (perWake ? 1 - 1 / nth : 1) : 0);
  const capturesDay = perWake * takingDay + extras * uploadsDay;
  const day =
    takingDay * partsBytes(mask) +
    uploadsDay * (extras ? partsBytes(extra) : 0) +
    perDay * RICH_WAKE_BYTES;
  return {
    perWake,
    extras,
    every: nth,
    wakesDay,
    uploadsDay,
    perDay,
    capturesDay,
    bytesWake: perWake ? partsBytes(mask) + RICH_WAKE_BYTES : 0,
    day,
    month: day * 30,
    radarSecondsWake: perWake * CAPTURE_SECONDS,
    radarDay: capturesDay * CAPTURE_SECONDS,
  };
}

// ---- Rich readings against the sun ----

// What the panel brought in a day lately: the readings' solar current (si,
// A) times the time to the next reading, summed over the last `days` and put
// to a day by the time they cover (a gap counts at most three spacings, at
// least 15 minutes: missing readings are no sun). Needs 21 hours or more.
// -> {mahDay, hours, readings} or null.
export const SOLAR_DAYS = 2;
export function solarInput(
  samples,
  { now = Date.now(), days = SOLAR_DAYS } = {},
) {
  const from = now - days * 86400000;
  const rows = (samples || [])
    .map((s) => ({
      t: timeOf(s?.timestamp),
      si: finite(s?.raw?.si) ?? finite(s?.raw?.solar_current),
    }))
    .filter(
      (r) =>
        r.t !== null && r.t >= from && r.t <= now && r.si !== null && r.si >= 0,
    )
    .sort((a, b) => a.t - b.t);
  if (rows.length < 2) return null;
  const gaps = rows
    .slice(1)
    .map((r, i) => r.t - rows[i].t)
    .filter((g) => g > 0)
    .sort((a, b) => a - b);
  const typical = gaps.length ? gaps[Math.floor(gaps.length / 2)] : 0;
  const cap = Math.max(3 * typical, 15 * 60000);
  let mah = 0;
  let ms = 0;
  rows.forEach((r, i) => {
    const next = rows[i + 1];
    const step = Math.min(next ? next.t - r.t : typical, cap);
    mah += (r.si * 1000 * step) / 3600000;
    ms += step;
  });
  if (ms < 21 * 3600000) return null;
  return {
    mahDay: (mah * 86400000) / ms,
    hours: ms / 3600000,
    readings: rows.length,
  };
}

// Above this share of the panel's daily input, rich readings may run the
// battery down.
export const RICH_SOLAR_SHARE = 0.6;

// The projection's rich line as a battery cost a day ({mahDay, mahCapture,
// capturesDay, baselineMahDay, sendMahDay, measured, scaled}): its own figure,
// which since the backend's .59 model is the captures (mAh a capture × captures
// a day) plus the energy meter's hourly baseline and, over LTE, each capture
// message's send (baselineMahDay, sendMahDay; null from an older server). Where
// the plan takes radar_rich on every Nth wake but the line still counts every
// wake (an older server: no `every` in it, its readings a day equal its wakes),
// the every-wake part put to one in N from its mAh a capture (`scaled`). Null
// without a line or a figure.
export function richDayCost(line, { every = 1 } = {}) {
  if (!line || line.off || !line.mahDay) return null;
  const nth = Math.max(1, Math.round(finite(every) ?? 1));
  const base = {
    mahDay: line.mahDay.high,
    mahCapture: line.mahCapture ?? null,
    capturesDay: line.capturesDay ?? null,
    baselineMahDay: finite(line.baselineMahDay),
    sendMahDay: finite(line.sendMahDay),
    // Firmware .60: the radar's rate, and a modelled capture's time at it.
    baud: finite(line.baud),
    captureS: finite(line.captureS),
    measured: line.batterySource === "measured",
    scaled: false,
  };
  const counted =
    nth > 1 &&
    finite(line.every) === null &&
    line.captures > 0 &&
    line.perDay !== null &&
    line.wakesDay !== null &&
    line.perDay >= 0.99 * line.wakesDay;
  if (!counted || line.mahCapture === null) return base;
  const upload = Math.max(0, (line.capturesUpload ?? 0) - line.captures);
  const capturesDay =
    (line.captures * line.wakesDay) / nth + upload * (line.uploadWakesDay ?? 0);
  const sendMahDay =
    base.sendMahDay !== null && base.capturesDay > 0
      ? (base.sendMahDay * capturesDay) / base.capturesDay
      : base.sendMahDay;
  return {
    ...base,
    mahDay:
      line.mahCapture * capturesDay +
      (base.baselineMahDay ?? 0) +
      (sendMahDay ?? 0),
    capturesDay,
    sendMahDay,
    scaled: true,
  };
}

// The rich line's battery a day in words, from richDayCost(): "0.733 mAh a
// capture (measured on this node) × 288 captures a day, plus 2.64 mAh for
// the energy meter's hourly baseline and 14.4 mAh to send the captures over
// cellular". `every`: the plan's radar_rich_every, named for a figure put to
// one in N here.
const mah = (v) =>
  `${v.toLocaleString(undefined, { maximumFractionDigits: v < 1 ? 3 : v < 10 ? 2 : 1 })} mAh`;
export function richCostText(cost, { every = 1 } = {}) {
  if (!cost) return "";
  // At a faster rate than the factory 9,600 baud, the rate; a modelled
  // capture's time at it, from the firmware's model.
  const at =
    cost.baud !== null && cost.baud !== undefined && cost.baud !== 9600
      ? ` at ${cost.baud.toLocaleString()} baud`
      : "";
  const how = cost.measured
    ? "measured on this node"
    : cost.captureS !== null && cost.captureS !== undefined
      ? `modelled, ${cost.captureS.toLocaleString()} s a capture, until the node has measured three rich readings`
      : "assumed until the node has measured three rich readings";
  const parts = [
    cost.mahCapture !== null
      ? `${mah(cost.mahCapture)} a capture${at} (${how})${cost.capturesDay !== null ? ` × ${Math.round(cost.capturesDay).toLocaleString()} captures a day` : ""}`
      : cost.capturesDay !== null
        ? `${Math.round(cost.capturesDay).toLocaleString()} captures a day`
        : "its battery line",
  ];
  const plus = [
    cost.baselineMahDay
      ? `${mah(cost.baselineMahDay)} for the energy meter's hourly baseline`
      : null,
    cost.sendMahDay
      ? `${mah(cost.sendMahDay)} to send the captures over cellular`
      : null,
  ].filter(Boolean);
  return `${parts[0]}${plus.length ? `, plus ${plus.join(" and ")}` : ""}${cost.scaled ? `; put to a reading on ${wakeEveryText(every)}, which the projection does not count yet` : ""}`;
}

// Whether the node skips rich readings for its battery: a readable cell (1.5
// V or more) under the floor skips them, and bursts too, whatever the input
// reads. -> {battery, floorV, low} or null without a reading.
export function richPower(sample, floorMv = RICH_FLOOR_DEFAULT_MV) {
  if (!sample) return null;
  const battery = [
    sample.battery_voltage,
    sample.raw?.bat_v,
    sample.raw?.battery_voltage,
  ]
    .map(finite)
    .find((v) => v !== null);
  const floorV = (finite(floorMv) ?? RICH_FLOOR_DEFAULT_MV) / 1000;
  const readable = battery !== undefined && battery !== null && battery >= 1.5;
  return {
    battery: readable ? battery : null,
    floorV,
    low: readable && battery < floorV,
    t: sample.timestamp ?? null,
  };
}

// ---- The bursts as the list shows them ----

// "rich" for a rich reading, "burst" for a classic burst (and older data
// without a kind).
export { burstKind };

// The clock hour (Singapore time) a time falls in, as its start in ms.
export const HOUR_MS = 3600000;
const SGT_MS = 8 * HOUR_MS;
export const hourOf = (ms) =>
  Math.floor((ms + SGT_MS) / HOUR_MS) * HOUR_MS - SGT_MS;

// Singapore clock times as the page's timestamps write them (fieldTime):
// "06:50 pm"; an hour of the list as "10 Oct, 06:00–07:00 pm" (or "10 Oct,
// 11:00 am–12:00 pm" across noon).
const CLOCK = new Intl.DateTimeFormat("en-SG", {
  timeZone: "Asia/Singapore",
  hour: "2-digit",
  minute: "2-digit",
});
const DAY = new Intl.DateTimeFormat("en-SG", {
  timeZone: "Asia/Singapore",
  day: "numeric",
  month: "short",
});
const clockParts = (ms) => {
  const parts = CLOCK.formatToParts(new Date(ms));
  const part = (type) => parts.find((p) => p.type === type)?.value ?? "";
  return {
    text: CLOCK.format(new Date(ms)),
    hm: `${part("hour")}:${part("minute")}`,
    period: part("dayPeriod"),
  };
};
export const clockText = (ms) => clockParts(ms).text;
// "10 Oct, 10:31 pm".
export const dayClockText = (ms) =>
  `${DAY.format(new Date(ms))}, ${clockText(ms)}`;
// An hour of the list in two parts, the day and the times, so the times can
// stay on one line: {day: "10 Oct", range: "11:00 pm–12:00 am"}.
export function hourParts(from, to = from + HOUR_MS) {
  const a = clockParts(from);
  const b = clockParts(to);
  return {
    day: DAY.format(new Date(from)),
    range: `${a.period && a.period === b.period ? a.hm : a.text}–${b.text}`,
  };
}
export function hourText(from, to = from + HOUR_MS) {
  const { day, range } = hourParts(from, to);
  return `${day}, ${range}`;
}

// The list's entries, newest first: the rich readings of each clock hour as
// one group (six or seven on a 10-minute wake), each classic burst on its
// own. `summaries`: burstSummary()s (with their kind), newest first. `held`:
// firmware .62's rich wakes the node held back (analysisPoints() with
// heldBack: no captures, so no burst of their own), listed in their hour as
// {id, kind "rich", held: true, t, ms, radarMm, ourMm, state, confidence}.
// -> [{key, kind: "hour" | "one", from, to, items (newest first), ms}]
export function listEntries(summaries, held = []) {
  const entries = [];
  const hours = new Map();
  const ids = new Set((summaries || []).map((s) => s?.id));
  for (const p of held || []) {
    if (!Number.isFinite(p?.ms) || ids.has(p.burstId)) continue;
    const item = {
      id: p.burstId,
      kind: "rich",
      held: true,
      t: p.t,
      ms: p.ms,
      radarMm: finite(p.radarMm),
      ourMm: finite(p.ourMm),
      state: p.state,
      confidence: finite(p.confidence),
    };
    const from = hourOf(p.ms);
    const group = hours.get(from);
    if (group) {
      group.items.push(item);
      group.ms = Math.max(group.ms, p.ms);
      continue;
    }
    const entry = {
      key: `hour-${from}`,
      kind: "hour",
      from,
      to: from + HOUR_MS,
      items: [item],
      ms: p.ms,
    };
    hours.set(from, entry);
    entries.push(entry);
  }
  for (const s of summaries || []) {
    if (s?.kind === "rich" && Number.isFinite(s.ms)) {
      const from = hourOf(s.ms);
      const group = hours.get(from);
      if (group) {
        group.items.push(s);
        group.ms = Math.max(group.ms, s.ms);
        continue;
      }
      const entry = {
        key: `hour-${from}`,
        kind: "hour",
        from,
        to: from + HOUR_MS,
        items: [s],
        ms: s.ms,
      };
      hours.set(from, entry);
      entries.push(entry);
    } else
      entries.push({
        key: `one-${s.id}`,
        kind: "one",
        from: s.ms,
        to: s.ms,
        items: [s],
        ms: s.ms,
      });
  }
  for (const e of entries)
    if (e.items.length > 1) e.items.sort((a, b) => b.ms - a.ms);
  return entries.sort((a, b) => (b.ms ?? -Infinity) - (a.ms ?? -Infinity));
}

// The window's rich readings: captures received and wakes, over the bursts
// whose first capture is at or after `from`.
export function richTally(summaries, from) {
  let wakes = 0;
  let captures = 0;
  for (const s of summaries || [])
    if (s?.kind === "rich" && s.ms >= from) {
      wakes += 1;
      captures += finite(s.received) ?? 0;
    }
  return { wakes, captures };
}
